import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { loadBlob, saveBlob } from '../lib/blobStore';
import {
  categoryDotClass,
  deleteFieldPhoto,
  getFieldPermissions,
  getFieldPhotoSignedUrl,
  listFieldPhotosForJob,
  type FieldPermissions,
  type FieldPhoto,
  type FieldPhotoDeviceInfo,
} from '../lib/fieldPhotos';
import { createSession } from '../lib/session';
import { enqueue, getItemsByJob, updateQueueItemDeviceInfo, type QueueItem } from '../lib/syncQueue';
import { getTaxonomy, type Taxonomy } from '../lib/taxonomy';

type PhotoSource = 'server' | 'local';
type LocalStatus = QueueItem['status'];

interface MergedPhoto {
  id: string; // photo_id
  sequence_index: number;
  captured_at: string | null;
  device_info: FieldPhotoDeviceInfo;
  source: PhotoSource; // 'server' = has DB row; 'local' = queue only
  localStatus?: LocalStatus; // if present in local queue
  localBlobKey?: string;
  storage_path?: string; // from server
  confirmed_at?: string | null;
  localQueueId?: string;
}

function mergePhotos(server: FieldPhoto[], local: QueueItem[]): MergedPhoto[] {
  const map = new Map<string, MergedPhoto>();
  for (const p of server) {
    map.set(p.id, {
      id: p.id,
      sequence_index: p.sequence_index,
      captured_at: p.captured_at,
      device_info: p.device_info,
      source: 'server',
      storage_path: p.storage_path,
      confirmed_at: p.confirmed_at,
    });
  }
  for (const q of local) {
    const existing = map.get(q.photo_id);
    if (existing) {
      existing.localStatus = q.status;
      existing.localBlobKey = q.local_blob_key;
      existing.localQueueId = q.id;
    } else {
      map.set(q.photo_id, {
        id: q.photo_id,
        sequence_index: q.sequence_index,
        captured_at: q.metadata.captured_at,
        device_info: (q.metadata.device_info ?? {}) as FieldPhotoDeviceInfo,
        source: 'local',
        localStatus: q.status,
        localBlobKey: q.local_blob_key,
        localQueueId: q.id,
      });
    }
  }
  return Array.from(map.values()).sort(
    (a, b) => a.sequence_index - b.sequence_index,
  );
}

function statusDotClass(p: MergedPhoto): string {
  if (p.source === 'server') return 'bg-eva-teal';
  switch (p.localStatus) {
    case 'confirmed':
      return 'bg-eva-teal';
    case 'uploading':
      return 'bg-bracket-blue';
    case 'failed':
      return 'bg-red-500';
    default:
      return 'bg-bracket-amber';
  }
}

function statusLabel(p: MergedPhoto): string {
  if (p.source === 'server') return 'synced';
  switch (p.localStatus) {
    case 'confirmed':
      return 'synced (local)';
    case 'uploading':
      return 'uploading';
    case 'failed':
      return 'failed';
    default:
      return 'pending';
  }
}

export function Photos() {
  const { jobId } = useParams<{ jobId: string }>();
  const [photos, setPhotos] = useState<MergedPhoto[] | null>(null);
  const [urls, setUrls] = useState<Record<string, string>>({});
  const [perms, setPerms] = useState<FieldPermissions | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [selectedIdx, setSelectedIdx] = useState<number | null>(null);
  const [importOpen, setImportOpen] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [labelQueueOpen, setLabelQueueOpen] = useState(false);
  const [taxonomy, setTaxonomy] = useState<Taxonomy | null>(null);
  const createdUrlsRef = useRef<string[]>([]);

  useEffect(() => {
    if (!jobId) return;
    let cancelled = false;
    (async () => {
      try {
        const [server, local, permissions] = await Promise.all([
          listFieldPhotosForJob(jobId),
          getItemsByJob(jobId),
          getFieldPermissions().catch(() => ({ allow_inspector_delete: false })),
        ]);
        if (cancelled) return;
        const merged = mergePhotos(server, local);
        setPhotos(merged);
        setPerms(permissions);

        // Load thumbnails: prefer local blob (fast, no network) else signed URL
        const entries = await Promise.all(
          merged.map(async (p) => {
            if (p.localBlobKey) {
              try {
                const blob = await loadBlob(p.localBlobKey);
                const u = URL.createObjectURL(blob);
                createdUrlsRef.current.push(u);
                return [p.id, u] as const;
              } catch {
                // blob missing — fall through to signed URL
              }
            }
            if (p.source === 'server') {
              try {
                const u = await getFieldPhotoSignedUrl(p.id);
                return [p.id, u] as const;
              } catch {
                return [p.id, ''] as const;
              }
            }
            return [p.id, ''] as const;
          }),
        );
        if (!cancelled) {
          setUrls(Object.fromEntries(entries.filter(([, v]) => v)));
        }
      } catch (e) {
        if (!cancelled) setErr((e as Error).message);
      }
    })();

    return () => {
      cancelled = true;
      createdUrlsRef.current.forEach((u) => URL.revokeObjectURL(u));
      createdUrlsRef.current = [];
    };
  }, [jobId, reloadKey]);

  const count = photos?.length ?? 0;
  const flaggedCount = useMemo(
    () => photos?.filter((p) => p.device_info.flagged).length ?? 0,
    [photos],
  );
  const unlabeled = useMemo(
    () =>
      (photos ?? []).filter((p) => {
        const di = p.device_info ?? {};
        const missingContext = !di.context?.category || !di.context?.label;
        const unlabeledStatus = !di.label_status || di.label_status === 'unlabeled';
        return missingContext || unlabeledStatus;
      }),
    [photos],
  );

  useEffect(() => {
    if (!labelQueueOpen || taxonomy) return;
    void getTaxonomy()
      .then((t) => setTaxonomy(t))
      .catch(() => setTaxonomy({ categories: [] }));
  }, [labelQueueOpen, taxonomy]);

  async function handleDelete(photo: MergedPhoto, reason: string) {
    if (photo.source !== 'server') {
      setErr('Local-only photos should be removed via Shoot Again during capture.');
      return;
    }
    await deleteFieldPhoto(photo.id, reason);
    setPhotos((prev) => prev?.filter((p) => p.id !== photo.id) ?? null);
    setSelectedIdx(null);
  }

  return (
    <div className="mx-auto max-w-3xl px-4 pb-6">
      <div className="mt-2 flex items-center justify-between">
        <Link to={`/job/${jobId}`} className="text-sm text-gray-600 underline">
          ‹ Job
        </Link>
        <div className="text-xs text-gray-500">
          {count} photo{count === 1 ? '' : 's'}
          {flaggedCount > 0 && (
            <span className="ml-2 text-red-500">{flaggedCount} flagged</span>
          )}
        </div>
      </div>
      <h1 className="mt-2 text-xl font-bold">Photos</h1>
      <div className="mt-3">
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setImportOpen(true)}
            disabled={!jobId}
            className="rounded-lg bg-eva-teal px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
          >
            Import Photos
          </button>
          {unlabeled.length > 0 && (
            <button
              onClick={() => setLabelQueueOpen(true)}
              className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-900"
            >
              Label Unlabeled Photos ({unlabeled.length})
            </button>
          )}
        </div>
      </div>

      {err && (
        <div className="mt-4 rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {err}
        </div>
      )}
      {!photos && !err && <div className="mt-4 text-sm text-gray-500">Loading…</div>}
      {photos && photos.length === 0 && (
        <div className="mt-6 rounded border border-dashed border-gray-300 p-6 text-center text-sm text-gray-500">
          No photos captured for this job yet.
          <div className="mt-2 text-xs text-gray-500">
            Add photos from your camera roll, then label them now or later.
          </div>
        </div>
      )}
      {photos && photos.length > 0 && (
        <div className="mt-4 grid grid-cols-3 gap-1">
          {photos.map((p, i) => {
            const url = urls[p.id];
            const dot = categoryDotClass(p.device_info.context?.category);
            const note = (p.device_info.note_text ?? '').length > 0;
            return (
              <button
                key={p.id}
                onClick={() => setSelectedIdx(i)}
                className="relative aspect-square overflow-hidden rounded bg-neutral-200"
              >
                {url ? (
                  <img
                    src={url}
                    alt={`Photo ${p.sequence_index}`}
                    className="h-full w-full object-cover"
                    loading="lazy"
                  />
                ) : (
                  <div className="flex h-full w-full items-center justify-center text-[10px] text-gray-400">
                    …
                  </div>
                )}
                <span className="absolute bottom-1 left-1 rounded bg-black/70 px-1.5 text-[10px] font-semibold text-white">
                  #{p.sequence_index}
                </span>
                <span
                  className={`absolute top-1 left-1 h-2.5 w-2.5 rounded-full ${dot}`}
                  title={p.device_info.context?.category ?? ''}
                />
                <span
                  className={`absolute top-1 right-1 h-2.5 w-2.5 rounded-full ${statusDotClass(p)}`}
                  title={statusLabel(p)}
                />
                {p.device_info.flagged && (
                  <span
                    className="absolute bottom-1 right-1 h-2.5 w-2.5 rounded-full bg-red-500"
                    title="flagged"
                  />
                )}
                {note && !p.device_info.flagged && (
                  <span
                    className="absolute bottom-1 right-1 h-2.5 w-2.5 rounded-full bg-bracket-amber"
                    title="has note"
                  />
                )}
              </button>
            );
          })}
        </div>
      )}

      {selectedIdx !== null && photos && photos[selectedIdx] && (
        <PhotoDetailModal
          photos={photos}
          urls={urls}
          index={selectedIdx}
          onClose={() => setSelectedIdx(null)}
          onNavigate={(next) => setSelectedIdx(next)}
          canDelete={!!perms?.allow_inspector_delete}
          onDelete={handleDelete}
        />
      )}
      {importOpen && jobId && (
        <ImportPhotosModal
          jobId={jobId}
          startSequenceIndex={
            (photos ?? []).reduce((max, p) => Math.max(max, p.sequence_index), 0) + 1
          }
          sessionId={sessionId}
          onSessionCreated={setSessionId}
          onClose={() => setImportOpen(false)}
          onImported={({ objectUrls }) => {
            setUrls((prev) => ({ ...prev, ...objectUrls }));
            createdUrlsRef.current.push(...Object.values(objectUrls));
            setReloadKey((n) => n + 1);
          }}
        />
      )}
      {labelQueueOpen && photos && taxonomy && (
        <LabelQueueModal
          items={unlabeled}
          urls={urls}
          taxonomy={taxonomy}
          onClose={() => {
            setLabelQueueOpen(false);
            setReloadKey((n) => n + 1);
          }}
        />
      )}
    </div>
  );
}

function splitDescription(v: string | undefined): string[] {
  if (!v) return [];
  return v.split(',').map((x) => x.trim()).filter(Boolean);
}

function LabelQueueModal({
  items,
  urls,
  taxonomy,
  onClose,
}: {
  items: MergedPhoto[];
  urls: Record<string, string>;
  taxonomy: Taxonomy;
  onClose: () => void;
}) {
  const [idx, setIdx] = useState(0);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const current = items[idx];
  const localOnly = current.source === 'local' && !!current.localQueueId;
  const categoryOptions = taxonomy.categories.map((c) => c.name);
  const initialCategory = current.device_info.context?.category ?? categoryOptions[0] ?? '';
  const [category, setCategory] = useState(initialCategory);
  const currentCat = taxonomy.categories.find((c) => c.name === category) ?? taxonomy.categories[0];
  const labelOptions = currentCat?.labels.map((l) => l.name) ?? [];
  const initialLabel = current.device_info.context?.label ?? labelOptions[0] ?? '';
  const [label, setLabel] = useState(initialLabel);
  const currentLabel = currentCat?.labels.find((l) => l.name === label);
  const [descriptions, setDescriptions] = useState<string[]>(
    splitDescription(current.device_info.context?.description),
  );
  const [damageStatus, setDamageStatus] = useState(current.device_info.damage_status ?? 'damage');
  const [noteText, setNoteText] = useState(current.device_info.note_text ?? '');
  const [flagged, setFlagged] = useState(current.device_info.flagged === true);

  useEffect(() => {
    const nextCategory = current.device_info.context?.category ?? taxonomy.categories[0]?.name ?? '';
    setCategory(nextCategory);
    const cat = taxonomy.categories.find((c) => c.name === nextCategory) ?? taxonomy.categories[0];
    const nextLabel = current.device_info.context?.label ?? cat?.labels[0]?.name ?? '';
    setLabel(nextLabel);
    setDescriptions(splitDescription(current.device_info.context?.description));
    setDamageStatus(current.device_info.damage_status ?? 'damage');
    setNoteText(current.device_info.note_text ?? '');
    setFlagged(current.device_info.flagged === true);
    setErr(null);
  }, [current, taxonomy]);

  useEffect(() => {
    const cat = taxonomy.categories.find((c) => c.name === category);
    if (!cat) return;
    if (!cat.labels.some((l) => l.name === label)) setLabel(cat.labels[0]?.name ?? '');
  }, [category, label, taxonomy.categories]);

  async function onSaveNext() {
    if (saving) return;
    if (!localOnly) {
      if (idx >= items.length - 1) onClose();
      else setIdx((n) => n + 1);
      return;
    }
    if (!category || !label) {
      setErr('Choose category and label.');
      return;
    }
    setSaving(true);
    setErr(null);
    try {
      const di = {
        ...(current.device_info ?? {}),
        context: {
          ...(current.device_info.context ?? {}),
          category,
          label,
          description: descriptions.join(', '),
        },
        damage_status: damageStatus,
        note_text: noteText,
        flagged,
        label_status: 'labeled',
      };
      await updateQueueItemDeviceInfo(current.localQueueId!, di as Record<string, unknown>);
      if (idx >= items.length - 1) onClose();
      else setIdx((n) => n + 1);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setSaving(false);
    }
  }

  const descriptionOptions = currentLabel?.descriptions ?? [];
  const doneLabel = idx >= items.length - 1 ? 'Save & Finish' : 'Save & Next';

  return (
    <div className="fixed inset-0 z-50 bg-black/50 p-3">
      <div className="mx-auto mt-6 max-w-md rounded-xl bg-white p-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-bold">Label Photos</h2>
          <button onClick={onClose} className="text-sm text-gray-500">Close</button>
        </div>
        <div className="mt-1 text-xs text-gray-500">
          {idx + 1} of {items.length}
        </div>
        {urls[current.id] && (
          <img src={urls[current.id]} alt="Current" className="mt-3 aspect-video w-full rounded-lg object-cover" />
        )}
        {!localOnly && (
          <div className="mt-3 rounded border border-amber-200 bg-amber-50 p-2 text-xs text-amber-800">
            This photo is already synced. Label updates from this screen are not available yet.
          </div>
        )}
        <div className="mt-3 space-y-3">
          <div>
            <div className="text-xs font-semibold text-gray-600">Category</div>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              disabled={!localOnly}
              className="mt-1 w-full rounded border border-gray-300 px-2 py-2 text-sm"
            >
              {categoryOptions.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>
          <div>
            <div className="text-xs font-semibold text-gray-600">Label</div>
            <select
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              disabled={!localOnly}
              className="mt-1 w-full rounded border border-gray-300 px-2 py-2 text-sm"
            >
              {labelOptions.map((l) => (
                <option key={l} value={l}>{l}</option>
              ))}
            </select>
          </div>
          {descriptionOptions.length > 0 && (
            <div>
              <div className="text-xs font-semibold text-gray-600">Descriptions</div>
              <div className="mt-1 flex flex-wrap gap-2">
                {descriptionOptions.map((d) => {
                  const on = descriptions.includes(d);
                  return (
                    <button
                      key={d}
                      type="button"
                      disabled={!localOnly}
                      onClick={() =>
                        setDescriptions((prev) =>
                          prev.includes(d) ? prev.filter((x) => x !== d) : [...prev, d],
                        )
                      }
                      className={`rounded-full border px-2 py-1 text-xs ${
                        on ? 'border-eva-teal bg-eva-teal/10 text-eva-teal' : 'border-gray-300 text-gray-700'
                      }`}
                    >
                      {d}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          <div>
            <div className="text-xs font-semibold text-gray-600">Damage</div>
            <div className="mt-1 flex gap-2">
              <button
                type="button"
                disabled={!localOnly}
                onClick={() => setDamageStatus('damage')}
                className={`flex-1 rounded border px-2 py-2 text-sm ${damageStatus === 'damage' ? 'border-eva-teal bg-eva-teal/10' : 'border-gray-300'}`}
              >
                Damage
              </button>
              <button
                type="button"
                disabled={!localOnly}
                onClick={() => setDamageStatus('no_damage')}
                className={`flex-1 rounded border px-2 py-2 text-sm ${damageStatus === 'no_damage' ? 'border-eva-teal bg-eva-teal/10' : 'border-gray-300'}`}
              >
                No Damage
              </button>
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={flagged} disabled={!localOnly} onChange={(e) => setFlagged(e.target.checked)} />
            Flag
          </label>
          <div>
            <div className="text-xs font-semibold text-gray-600">Note</div>
            <textarea
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              disabled={!localOnly}
              rows={2}
              className="mt-1 w-full rounded border border-gray-300 px-2 py-2 text-sm"
              placeholder="Add note..."
            />
          </div>
        </div>
        {err && <div className="mt-3 rounded border border-red-200 bg-red-50 p-2 text-xs text-red-700">{err}</div>}
        <button
          type="button"
          onClick={() => void onSaveNext()}
          disabled={saving}
          className="mt-4 w-full rounded-lg bg-eva-teal px-4 py-3 text-sm font-semibold text-white disabled:opacity-60"
        >
          {saving ? 'Saving…' : doneLabel}
        </button>
      </div>
    </div>
  );
}

type ImportIntent = 'upload_only' | 'label_now' | 'group_first';

function checksumHex(buffer: ArrayBuffer): Promise<string> {
  return crypto.subtle.digest('SHA-256', buffer).then((hashBuf) =>
    Array.from(new Uint8Array(hashBuf))
      .map((b) => b.toString(16).padStart(2, '0'))
      .join(''),
  );
}

function ImportPhotosModal({
  jobId,
  startSequenceIndex,
  sessionId,
  onSessionCreated,
  onClose,
  onImported,
}: {
  jobId: string;
  startSequenceIndex: number;
  sessionId: string | null;
  onSessionCreated: (sessionId: string) => void;
  onClose: () => void;
  onImported: (args: { objectUrls: Record<string, string> }) => void;
}) {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [files, setFiles] = useState<File[]>([]);
  const [intent, setIntent] = useState<ImportIntent>('upload_only');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [completedCount, setCompletedCount] = useState(0);

  async function runImport() {
    if (busy || files.length === 0) return;
    setBusy(true);
    setErr(null);
    try {
      const sessId = sessionId ?? (await createSession({ job_id: jobId, mode: 'new' })).id;
      if (!sessionId) onSessionCreated(sessId);
      const objectUrls: Record<string, string> = {};

      for (const [idx, file] of files.entries()) {
        const photoId = crypto.randomUUID();
        const capturedAt = new Date(file.lastModified || Date.now()).toISOString();
        const originalTakenAt = file.lastModified ? new Date(file.lastModified).toISOString() : null;
        const checksum = await checksumHex(await file.arrayBuffer());
        const blobKey = `${photoId}.jpg`;
        await saveBlob(blobKey, file);
        const item: QueueItem = {
          id: crypto.randomUUID(),
          org_id: '',
          job_id: jobId,
          session_id: sessId,
          photo_id: photoId,
          sequence_index: startSequenceIndex + idx,
          local_blob_key: blobKey,
          metadata: {
            captured_at: capturedAt,
            checksum,
            device_info: {
              ua: navigator.userAgent,
              source: 'camera_roll_import',
              import_intent: intent,
              original_taken_at: originalTakenAt,
              original_filename: file.name,
              label_status: 'unlabeled',
              context: null,
              flagged: false,
              note_text: '',
            },
          },
          status: 'pending',
          attempts: 0,
          last_attempt_at: null,
          confirmed_at: null,
          error_detail: null,
          created_at: new Date().toISOString(),
        };
        await enqueue({
          id: item.id,
          org_id: item.org_id,
          job_id: item.job_id,
          session_id: item.session_id,
          photo_id: item.photo_id,
          sequence_index: item.sequence_index,
          local_blob_key: item.local_blob_key,
          metadata: item.metadata,
        });
        objectUrls[photoId] = URL.createObjectURL(file);
      }
      onImported({ objectUrls });
      setCompletedCount(files.length);
      setFiles([]);
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  if (completedCount > 0) {
    return (
      <div className="fixed inset-0 z-50 bg-black/50 p-4">
        <div className="mx-auto mt-8 max-w-md rounded-xl bg-white p-4">
          <h2 className="text-lg font-bold">{completedCount} photos added</h2>
          <p className="mt-1 text-sm text-gray-600">
            They’re saved to the file as unlabeled photos.
          </p>
          <div className="mt-4 space-y-2">
            <button
              type="button"
              onClick={onClose}
              className="w-full rounded-lg bg-eva-teal px-4 py-3 text-sm font-semibold text-white"
            >
              View photos
            </button>
            <button
              type="button"
              onClick={() => {
                setCompletedCount(0);
                setIntent('upload_only');
                setErr(null);
                window.setTimeout(() => fileInputRef.current?.click(), 0);
              }}
              className="w-full rounded-lg border border-gray-300 px-4 py-3 text-sm font-semibold text-gray-900"
            >
              Import more
            </button>
            <button
              type="button"
              disabled
              className="w-full rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm font-semibold text-gray-400"
            >
              Label now — coming next
            </button>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            onChange={(e) => setFiles(Array.from(e.target.files ?? []))}
            className="sr-only"
          />
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/50 p-4">
      <div className="mx-auto mt-8 max-w-md rounded-xl bg-white p-4">
        <h2 className="text-lg font-bold">Import Photos</h2>
        <p className="mt-1 text-sm text-gray-600">
          Add photos from your camera roll, then label them now or later.
        </p>
        <div className="mt-3 rounded-lg border border-dashed border-gray-300 p-3">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            onChange={(e) => setFiles(Array.from(e.target.files ?? []))}
            className="sr-only"
          />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={busy}
            className="w-full rounded-lg border border-gray-300 bg-gray-50 px-4 py-3 text-sm font-semibold text-gray-900 disabled:opacity-60"
          >
            Choose Photos
          </button>
          <div className="mt-2 text-center text-xs text-gray-500">
            {files.length === 0
              ? 'Select photos from your camera roll.'
              : `${files.length} selected. Original order is preserved.`}
          </div>
          {files.length > 0 && (
            <button
              type="button"
              onClick={() => {
                setFiles([]);
                if (fileInputRef.current) fileInputRef.current.value = '';
              }}
              disabled={busy}
              className="mt-2 w-full text-xs font-semibold text-gray-500 underline disabled:opacity-60"
            >
              Clear selection
            </button>
          )}
        </div>
        {files.length > 0 && (
          <div className="mt-4 space-y-2">
            <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
              What next?
            </div>
            <label className="flex items-start gap-2 rounded border p-2 text-sm">
              <input
                type="radio"
                name="import-intent"
                checked={intent === 'upload_only'}
                onChange={() => setIntent('upload_only')}
              />
              <span>
                <span className="font-semibold">Save for later</span> — add these to the file as unlabeled photos.
              </span>
            </label>
            <label className="flex items-start gap-2 rounded border p-2 text-sm opacity-75">
              <input
                type="radio"
                name="import-intent"
                checked={intent === 'label_now'}
                onChange={() => setIntent('label_now')}
              />
              <span>
                <span className="font-semibold">Label now</span> — review each photo and add labels. Coming next.
              </span>
            </label>
            <label className="flex items-start gap-2 rounded border p-2 text-sm opacity-75">
              <input
                type="radio"
                name="import-intent"
                checked={intent === 'group_first'}
                onChange={() => setIntent('group_first')}
              />
              <span>
                <span className="font-semibold">Group first</span> — sort photos by area before labeling. Coming next.
              </span>
            </label>
          </div>
        )}
        {err && <div className="mt-3 rounded border border-red-200 bg-red-50 p-2 text-xs text-red-700">{err}</div>}
        <div className="mt-4 flex gap-2">
          <button
            onClick={onClose}
            disabled={busy}
            className="flex-1 rounded border border-gray-300 px-3 py-2 text-sm disabled:opacity-60"
          >
            Cancel
          </button>
          <button
            onClick={() => void runImport()}
            disabled={busy || files.length === 0}
            className="flex-1 rounded bg-eva-teal px-3 py-2 text-sm font-semibold text-white disabled:opacity-60"
          >
            {busy ? 'Saving…' : 'Continue'}
          </button>
        </div>
      </div>
    </div>
  );
}

function PhotoDetailModal({
  photos,
  urls,
  index,
  onClose,
  onNavigate,
  canDelete,
  onDelete,
}: {
  photos: MergedPhoto[];
  urls: Record<string, string>;
  index: number;
  onClose: () => void;
  onNavigate: (next: number) => void;
  canDelete: boolean;
  onDelete: (photo: MergedPhoto, reason: string) => Promise<void>;
}) {
  const photo = photos[index];
  const url = urls[photo.id];
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [reason, setReason] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const di = photo.device_info;
  const captured = photo.captured_at ? new Date(photo.captured_at) : null;

  async function confirm() {
    if (deleting) return;
    setDeleting(true);
    setErr(null);
    try {
      await onDelete(photo, reason);
    } catch (e) {
      setErr((e as Error).message);
      setDeleting(false);
    }
  }

  const canPrev = index > 0;
  const canNext = index < photos.length - 1;

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-black text-white">
      <div className="flex items-center justify-between p-4">
        <button
          onClick={onClose}
          className="rounded-full bg-white/15 px-3 py-1.5 text-sm"
        >
          ‹ Gallery
        </button>
        <div className="text-sm">
          #{photo.sequence_index}
          <span className="ml-2 text-xs opacity-60">
            {index + 1} of {photos.length}
          </span>
        </div>
      </div>

      <div className="relative flex-1">
        {url ? (
          <img
            src={url}
            alt={`Photo ${photo.sequence_index}`}
            className="absolute inset-0 h-full w-full object-contain"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-sm opacity-60">
            image unavailable
          </div>
        )}
        {canPrev && (
          <button
            onClick={() => onNavigate(index - 1)}
            className="absolute top-1/2 left-2 -translate-y-1/2 rounded-full bg-black/60 px-3 py-2 text-lg"
            aria-label="Previous"
          >
            ‹
          </button>
        )}
        {canNext && (
          <button
            onClick={() => onNavigate(index + 1)}
            className="absolute top-1/2 right-2 -translate-y-1/2 rounded-full bg-black/60 px-3 py-2 text-lg"
            aria-label="Next"
          >
            ›
          </button>
        )}
      </div>

      <div className="space-y-2 bg-black/85 p-4 text-sm">
        {di.context && (
          <div>
            <div className="text-[10px] uppercase tracking-wide opacity-60">Context</div>
            <div>
              {di.context.category} · {di.context.label}
            </div>
            {di.context.description && (
              <div className="text-xs opacity-80">{di.context.description}</div>
            )}
          </div>
        )}

        <div className="flex flex-wrap items-center gap-2 text-xs opacity-85">
          {captured && (
            <span>
              <span className="text-eva-teal">App Verified</span> · {captured.toLocaleString()}
            </span>
          )}
          {di.gps && (
            <span>
              GPS {di.gps.lat.toFixed(5)}, {di.gps.lng.toFixed(5)}
            </span>
          )}
          {di.flagged && (
            <span className="rounded-full bg-red-500 px-2 py-0.5 font-semibold">flagged</span>
          )}
        </div>

        {di.note_text && (
          <div>
            <div className="text-[10px] uppercase tracking-wide opacity-60">Note</div>
            <div className="text-xs opacity-90">{di.note_text}</div>
          </div>
        )}

        {canDelete && photo.source === 'server' && (
          <div className="pt-2">
            {!confirmDelete ? (
              <button
                onClick={() => setConfirmDelete(true)}
                className="rounded-lg border border-red-400/50 bg-red-500/10 px-3 py-2 text-sm font-semibold text-red-300"
              >
                Delete photo
              </button>
            ) : (
              <div className="rounded-lg border border-red-400/50 bg-red-500/10 p-3">
                <div className="text-sm font-semibold text-red-200">
                  Delete photo #{photo.sequence_index}?
                </div>
                <div className="mt-1 text-xs text-red-200/80">
                  This creates a gap in the sequence. You can close gaps during Wrap Up.
                </div>
                <input
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Reason (optional)"
                  disabled={deleting}
                  className="mt-2 w-full rounded border border-red-300/40 bg-black/40 px-2 py-1 text-xs text-white outline-none"
                />
                {err && <div className="mt-2 text-xs text-red-300">{err}</div>}
                <div className="mt-2 flex gap-2">
                  <button
                    onClick={() => {
                      setConfirmDelete(false);
                      setReason('');
                      setErr(null);
                    }}
                    disabled={deleting}
                    className="flex-1 rounded bg-white/15 py-1.5 text-sm disabled:opacity-40"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => void confirm()}
                    disabled={deleting}
                    className="flex-1 rounded bg-red-500 py-1.5 text-sm font-semibold text-white disabled:opacity-40"
                  >
                    {deleting ? 'Deleting…' : 'Delete'}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
